<template>
    <div class="sf-page">
        <div class="sf-header">
            <div class="sf-header-left">
                <div class="sf-icon-wrap"><i class="fas fa-clock"></i></div>
                <h2 class="sf-title">Attendance Shifts</h2>
                <span class="sf-count" v-if="items.length">{{ items.length }}</span>
            </div>
            <AllowPermission :permission="PERMISSION_CODES.EDIT_ATTENDANCE_SHIFTS">
                <button class="sf-add-btn" @click="showForm = true">
                    <i class="fas fa-plus"></i>
                    Add Shift
                </button>
            </AllowPermission>
        </div>

        <RightSideModal :show="showForm" :title="editMode ? 'Edit Shift' : 'New Shift'" @close="closeForm">
            <Form :fields="createFields" title="Attendance Shift" submitUrl="/attendance-shifts"
                updateRootUrl="/attendance-shifts" @success="handleFormSuccess" @cancel="closeForm"
                :editMode="editMode" :editData="editData" />
        </RightSideModal>

        <div v-if="loading" class="sf-loading"><div class="sf-spinner"></div><span>Loading...</span></div>
        <div v-else-if="!items.length" class="sf-empty">
            <div class="sf-empty-icon"><i class="fas fa-clock"></i></div>
            <h3>No shifts found</h3>
        </div>
        <div v-else class="sf-grid">
            <div class="sf-card" v-for="(item, idx) in items" :key="item.id" :style="{ animationDelay: `${idx * 0.03}s` }">
                <div class="sf-card-top">
                    <div class="sf-card-icon" :class="sfColor(idx)"><i class="fas fa-business-time"></i></div>
                    <div class="sf-card-info">
                        <h4 class="sf-card-name">{{ item.name }}</h4>
                        <span class="sf-default-badge" v-if="item.isDefault"><i class="fas fa-star"></i> Default</span>
                    </div>
                </div>
                <div class="sf-card-details">
                    <div class="sf-detail">
                        <span class="sf-detail-label">Start</span>
                        <span class="sf-detail-val"><i class="fas fa-play"></i> {{ item.startTime || '—' }}</span>
                    </div>
                    <div class="sf-detail">
                        <span class="sf-detail-label">End</span>
                        <span class="sf-detail-val"><i class="fas fa-stop"></i> {{ item.endTime || '—' }}</span>
                    </div>
                    <div class="sf-detail">
                        <span class="sf-detail-label">Duration</span>
                        <span class="sf-detail-val"><i class="fas fa-hourglass-half"></i> {{ item.durationFormatted || '—' }}</span>
                    </div>
                </div>
                <div class="sf-weekdays" v-if="item.weekDays && item.weekDays.length">
                    <span v-for="d in dayLabels" :key="d.id" class="sf-day" :class="{ 'sf-day-active': item.weekDays.includes(d.id) }">{{ d.short }}</span>
                </div>
                <div class="sf-card-actions">
                    <AllowPermission :permission="PERMISSION_CODES.EDIT_ATTENDANCE_SHIFTS">
                        <button class="sf-action-btn sf-btn-edit" @click="enableEditing(item)"><i class="fas fa-pen"></i> Edit</button>
                    </AllowPermission>
                    <AllowPermission :permission="PERMISSION_CODES.EDIT_ATTENDANCE_SHIFTS">
                        <button class="sf-action-btn sf-btn-delete" @click="deleteItem(item.id)"><i class="fas fa-trash-alt"></i> Delete</button>
                    </AllowPermission>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Form from "../../components/crud/form.vue"
import RightSideModal from "../../components/layout/RightSideModal.vue"
import AllowPermission from "../../components/layout/AllowPermission.vue"
import { createField } from "../../utils/common"
import { PERMISSION_CODES } from "../../store/permission"
import { http } from "../../utils/http"

export default {
    data() {
        return {
            PERMISSION_CODES,
            createFields: [
                createField("Name*", "name", "text", true),
                createField("Select Weekdays*", "weekDays", "multiselect", true, "", [
                    { id: 0, title: "Sunday" }, { id: 1, title: "Monday" }, { id: 2, title: "Tuesday" },
                    { id: 3, title: "Wednesday" }, { id: 4, title: "Thursday" }, { id: 5, title: "Friday" }, { id: 6, title: "Saturday" },
                ]),
                createField("Start Time*", "startTime", "time", true),
                createField("End Time*", "endTime", "time", true),
                createField("Is Default", "isDefault", "switch", false, false),
            ],
            dayLabels: [
                { id: 0, short: 'Su' }, { id: 1, short: 'Mo' }, { id: 2, short: 'Tu' },
                { id: 3, short: 'We' }, { id: 4, short: 'Th' }, { id: 5, short: 'Fr' }, { id: 6, short: 'Sa' },
            ],
            items: [],
            loading: true,
            showForm: false,
            editMode: false,
            editData: null,
        }
    },
    components: { Form, RightSideModal, AllowPermission },
    mounted() { this.fetchData() },
    methods: {
        sfColor(i) { return ["sf-clr-blue","sf-clr-purple","sf-clr-teal","sf-clr-amber","sf-clr-rose"][i % 5] },
        closeForm() { this.showForm = false; this.editMode = false; this.editData = null },
        handleFormSuccess() { this.closeForm(); this.fetchData() },
        enableEditing(item) { this.editMode = true; this.editData = { ...item }; this.showForm = true; window.scrollTo({ top: 0, behavior: "smooth" }) },
        async fetchData() {
            this.loading = true
            try { const res = await http.get("/attendance-shifts"); this.items = res.data.result || [] } catch (err) { console.log(err) }
            this.loading = false
        },
        async deleteItem(id) {
            if (!confirm("Delete this shift?")) return
            try { await http.delete(`/attendance-shifts/${id}`); this.$toast.success("Deleted"); this.items = this.items.filter(c => c.id !== id) }
            catch (e) { this.$toast.error("Error deleting") }
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";
.sf-page { animation: sfFade .3s ease; }
@keyframes sfFade { from { opacity:0; transform:translateY(4px); } to { opacity:1; transform:translateY(0); } }
.sf-header { display:flex; align-items:center; justify-content:space-between; gap:12px; background:white; border:1px solid $gray100; border-radius:8px; padding:10px 16px; margin-bottom:12px; box-shadow:$shadowSm; flex-wrap:wrap; }
.sf-header-left { display:flex; align-items:center; gap:10px; }
.sf-icon-wrap { width:32px; height:32px; border-radius:8px; background:linear-gradient(135deg,$primaryLight,darken($primaryLight,4%)); color:$primaryColor; display:flex; align-items:center; justify-content:center; font-size:14px; }
.sf-title { font-size:15px; font-weight:700; color:$gray800; margin:0; }
.sf-count { min-width:20px; height:20px; padding:0 6px; border-radius:$radiusFull; font-size:11px; font-weight:700; background:$primaryColor; color:white; display:inline-flex; align-items:center; justify-content:center; }
.sf-add-btn { display:inline-flex; align-items:center; gap:6px; padding:7px 14px; border:none; border-radius:6px; background:$primaryColor; color:white; font-size:12.5px; font-weight:600; cursor:pointer; transition:all $transitionFast; font-family:inherit; &:hover { background:$primaryDark; } }
.sf-loading { display:flex; align-items:center; justify-content:center; gap:10px; padding:64px 16px; background:white; border:1px solid $gray100; border-radius:8px; box-shadow:$shadowSm; span { font-size:13px; color:$gray400; } }
.sf-spinner { width:24px; height:24px; border:2.5px solid $gray200; border-top-color:$primaryColor; border-radius:50%; animation:sfSpin .7s linear infinite; }
@keyframes sfSpin { to { transform:rotate(360deg); } }
.sf-empty { text-align:center; padding:64px 16px; background:white; border:1px solid $gray100; border-radius:8px; box-shadow:$shadowSm; h3 { font-size:14px; font-weight:700; color:$gray600; margin:0; } }
.sf-empty-icon { width:48px; height:48px; border-radius:$radiusFull; background:$gray50; border:1px solid $gray100; display:flex; align-items:center; justify-content:center; margin:0 auto 12px; i { font-size:20px; color:$gray300; } }
.sf-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:10px; }
.sf-card { background:white; border:1px solid $gray100; border-radius:10px; padding:14px; box-shadow:$shadowSm; display:flex; flex-direction:column; gap:10px; animation:sfSlide .25s ease both; transition:all $transitionFast; &:hover { border-color:$gray200; box-shadow:$shadowMd; transform:translateY(-1px); } }
@keyframes sfSlide { from { opacity:0; transform:translateY(4px); } to { opacity:1; transform:translateY(0); } }
.sf-card-top { display:flex; align-items:center; gap:10px; }
.sf-card-icon { width:36px; height:36px; border-radius:8px; display:flex; align-items:center; justify-content:center; font-size:14px; flex-shrink:0; }
.sf-clr-blue { background:#eff6ff; color:#2563eb; }
.sf-clr-purple { background:#f5f3ff; color:#7c3aed; }
.sf-clr-teal { background:#f0fdfa; color:#0d9488; }
.sf-clr-amber { background:#fffbeb; color:#d97706; }
.sf-clr-rose { background:#fff1f2; color:#e11d48; }
.sf-card-info { flex:1; min-width:0; display:flex; align-items:center; gap:8px; }
.sf-card-name { font-size:13.5px; font-weight:700; color:$gray800; margin:0; }
.sf-default-badge { font-size:10px; font-weight:700; padding:2px 8px; border-radius:$radiusFull; background:#fef3c7; color:#d97706; display:inline-flex; align-items:center; gap:3px; i { font-size:8px; } }

/* Details */
.sf-card-details { display:flex; flex-wrap:wrap; gap:6px; }
.sf-detail { flex:1; min-width:70px; background:$gray50; border:1px solid $gray100; border-radius:6px; padding:6px 10px; display:flex; flex-direction:column; gap:2px; }
.sf-detail-label { font-size:10px; color:$gray400; font-weight:500; text-transform:uppercase; letter-spacing:0.3px; }
.sf-detail-val { font-size:12px; color:$gray700; font-weight:600; display:flex; align-items:center; gap:4px; i { font-size:10px; color:$gray400; } }

/* Weekday pills */
.sf-weekdays { display:flex; gap:4px; }
.sf-day { width:28px; height:28px; border-radius:$radiusFull; display:flex; align-items:center; justify-content:center; font-size:10px; font-weight:700; border:1px solid $gray200; color:$gray400; background:white; transition:all $transitionFast; }
.sf-day-active { background:$primaryColor; color:white; border-color:$primaryColor; }

/* Actions */
.sf-card-actions { display:flex; gap:6px; margin-top:auto; padding-top:6px; border-top:1px solid $gray100; }
.sf-action-btn { display:inline-flex; align-items:center; gap:4px; padding:4px 10px; border:1px solid $gray200; border-radius:5px; font-size:11px; font-weight:600; cursor:pointer; transition:all $transitionFast; font-family:inherit; background:white; i { font-size:10px; } }
.sf-btn-edit { color:$primaryColor; &:hover { background:$primaryLight; border-color:rgba(0,94,137,0.15); } }
.sf-btn-delete { color:$danger; &:hover { background:#fef2f2; border-color:rgba(220,38,38,0.15); } }
@media(max-width:992px) { .sf-grid { grid-template-columns:repeat(2,1fr); } }
@media(max-width:480px) { .sf-grid { grid-template-columns:1fr; } }
</style>
